import { useEffect } from 'react'

import type { BadgeProps } from './badge.types'

import { vui } from '../core'
import { Tag } from '../tag'

/**
 * Badge is a non-interactive, pill-shaped alias of {@link Tag}.
 *
 * Appearance is controlled by two props:
 *  - `variant`: `'subtle' | 'solid'`
 *  - `color`:   `'grey' | 'green' | 'yellow' | 'red' | 'blue'`
 *
 * Use `icon` to place an icon on the leading side of the label.
 * Badge fully delegates theming to Tag.
 */

const HIERARCHY = ['subtle', 'solid'] as const

export const Badge = vui<'span', BadgeProps>((props, ref) => {
  const { variant: variantProp, color, icon, iconLeft, label, text, ...rest } = props

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && iconLeft !== undefined) {
      console.warn('[Badge] `iconLeft` is deprecated. Use `icon` instead.')
    }
  }, [iconLeft])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && text !== undefined) {
      console.warn('[Badge] `text` is deprecated. Use `label` instead.')
    }
  }, [text])

  const resolvedIcon = icon ?? iconLeft
  const resolvedLabel = label ?? text
  const hierarchy = (HIERARCHY as readonly string[]).includes(variantProp ?? '') ? variantProp : 'subtle'

  return (
    <Tag
      borderRadius="9999px"
      className="vui-badge"
      color={color ?? 'grey'}
      isInteractive={false}
      minW="20px"
      ref={ref}
      startIcon={resolvedIcon}
      text={resolvedLabel}
      variant={hierarchy as 'subtle' | 'solid'}
      {...rest}
      onDelete={undefined}
    />
  )
})

Badge.displayName = 'Badge'
export default Badge
